import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PreconnectService {
  private initialized = false;

  constructor(
    @Inject(DOCUMENT) private document: Document
  ) {}

  /**
   * Adds preconnect and dns-prefetch links based on environment configuration
   * Should be called early in app initialization
   */
  initializePreconnects(): void {
    // Prevent duplicate initialization
    if (this.initialized) return;
    this.initialized = true;

    // For SSR, we can still add links - Angular Universal will include them in rendered HTML
    this.addPreconnects();
  }

  private addPreconnects(): void {
    const head = this.document.head;
    if (!head) return;

    const preconnectOrigins = this.getPreconnectOrigins();

    preconnectOrigins.forEach(origin => {
      // Check if link already exists to avoid duplicates
      const existingLink = head.querySelector(`link[rel="preconnect"][href="${origin.url}"]`);
      if (existingLink) return;

      const link = this.document.createElement('link');
      link.rel = 'preconnect';
      link.href = origin.url;
      if (origin.crossorigin) {
        link.setAttribute('crossorigin', '');
      }
      // Insert at the beginning of head for early connection establishment
      head.insertBefore(link, head.firstChild);
    });

    // Add dns-prefetch for lower priority origins
    const dnsPrefetchOrigins = this.getDnsPrefetchOrigins();
    dnsPrefetchOrigins.forEach(origin => {
      const existingLink = head.querySelector(`link[rel="dns-prefetch"][href="${origin}"]`);
      if (existingLink) return;

      const link = this.document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = origin;
      head.insertBefore(link, head.firstChild);
    });
  }

  private getPreconnectOrigins(): Array<{ url: string; crossorigin?: boolean }> {
    const origins: Array<{ url: string; crossorigin?: boolean }> = [];

    // Environment-specific origins from config
    if (environment.preconnects?.origins) {
      environment.preconnects.origins.forEach(url => {
        origins.push({ url });
      });
    }

    // Extract domain from Firebase authDomain if available
    if (environment.apis?.firebase?.authDomain) {
      const firebaseDomain = `https://${environment.apis.firebase.authDomain}`;
      origins.push({ url: firebaseDomain });
    }

    // Add Google APIs for Firebase
    origins.push({ url: 'https://www.googleapis.com' });

    return origins;
  }

  private getDnsPrefetchOrigins(): string[] {
    return environment.preconnects?.dnsPrefetch || [];
  }
}

